import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { canStartSongPublish } from 'fym-shared/src/music24/songPublish';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { useGetSongPublishDefaultsQuery } from '../../../../redux/api/fymAPI';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { IntegrationTypeIcon } from '../../components/IntegrationTypeIcon/IntegrationTypeIcon';
import { useOpenSongPage } from '../../../smartLinks/catalog/song/page/useOpenSongPage';
import { masterCheckOf } from '../songUpload';
import { soundCloudUploadMessages as m } from '../soundCloudMessages';
import { SongUploadForm } from './SongUploadForm';
import { SongUploadMasterCheck } from './SongUploadMasterCheck';
import { SongUploadStatus } from './SongUploadStatus';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for SongUpload; each part uses its own slice of it */
import styles from './SongUpload.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongUploadDialogProps {
  accountId: number;
  catalogItemUuid: string;
  /** `status`: Status of the row (c6). `upload`: Upload (c3, or c5 with no usable master). */
  mode: 'upload' | 'status';
  /** Starts the SoundCloud sign-in with `returnTo` = this page. */
  onConnect: () => void;
  onClose: () => void;
}

/**
 * The SoundCloud upload of one song (design c3-c6), opened from the SoundCloud row of "Where it
 * plays". It reads `GET .../song-publishes/defaults` and shows the status of a live or waiting
 * upload, the master check, the sign-in, or the form. Full screen below 576 px.
 */
const SongUploadDialogComponent: React.FC<SongUploadDialogProps> = ({
  accountId,
  catalogItemUuid,
  mode,
  onConnect,
  onClose,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openSong = useOpenSongPage();
  const [wantsForm, setWantsForm] = useState(mode === 'upload');
  const { data, error, isLoading, refetch } = useGetSongPublishDefaultsQuery({
    accountId,
    catalogItemUuid,
    channel: 'soundcloud',
  });
  const openCatalog = () => {
    onClose();
    router.push(SMLRoutes.catalog);
  };
  const openSongPage = () => {
    onClose();
    openSong(catalogItemUuid, 'files');
  };

  let body: React.ReactNode;
  if (isLoading) {
    body = <LoadingView />;
  } else if (error !== undefined || data === undefined) {
    body = <ErrorView error={error} retry={() => refetch().catch(console.error)} />;
  } else if (data.current !== null && (!canStartSongPublish(data.current) || !wantsForm)) {
    body = (
      <SongUploadStatus
        accountId={accountId}
        record={data.current}
        releaseDate={data.facts.releaseDate}
        loginExpired={data.connection.needsReconnect}
        onUploadAgain={() => setWantsForm(true)}
        onReconnect={onConnect}
        onClose={onClose}
      />
    );
  } else if (!data.connection.connected || data.connection.needsReconnect) {
    body = (
      <>
        <ModalBody>
          <Alert color="info" className="mb-0">
            {intl.formatMessage(m.connectFirst)}
          </Alert>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn btn-primary" onClick={onConnect}>
            {intl.formatMessage(data.connection.needsReconnect ? m.reconnect : m.connect)}
          </button>
        </ModalFooter>
      </>
    );
  } else {
    const check = masterCheckOf(data.facts);
    body =
      check === null ? (
        <SongUploadForm accountId={accountId} defaults={data} onOpenCatalog={openCatalog} onClose={onClose} />
      ) : (
        <SongUploadMasterCheck check={check} onOpenSongPage={openSongPage} />
      );
  }

  return (
    <Modal isOpen toggle={onClose} centered scrollable fullscreen="sm" aria-labelledby="song-upload-title">
      <div className="modal-header">
        <div className={styles.head}>
          <IntegrationTypeIcon integrationType={MusicAPIIntegrationID.SoundCloud} size={36} />
          <div className={styles.headText}>
            <b id="song-upload-title">{intl.formatMessage(m.title)}</b>
            {data !== undefined && (
              <small>
                {intl.formatMessage(m.songLine, { title: data.facts.title, artist: data.facts.artistName })}
              </small>
            )}
          </div>
        </div>
        <button type="button" className="btn-close" aria-label={intl.formatMessage(m.close)} onClick={onClose} />
      </div>
      {body}
    </Modal>
  );
};

export const SongUploadDialog = TypedMemo(SongUploadDialogComponent);
